import {faExchangeAlt} from '@fortawesome/free-solid-svg-icons';
import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {withTranslation} from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import 'react-placeholder/lib/reactPlaceholder.css';
import {useTable} from 'react-table';
import Error from '../Error/Error';
import {withTheme} from '../HOC/withTheme';
import {CenteredTd} from '../StyledComp';
import {Td, BorderedTable, Thead, Th, PointerTh, SortIcon, Tr} from './DatatableStyles';


const DatatableCore = ({t, columns, theme, onClickSortable, forceLoading, loading, error, result, limit}) => {

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
  } = useTable(
    {
      columns,
      data: result.rows || [],
    },
  );

  const displayLoading = () => {
    const loadingColumns = columns.map((_, index) => (
      <Td key={index}>
        <ReactPlaceholder
          showLoadingAnimation
          ready={false}
          rows={1}
          type={'textRow'}
          style={{height: '0.875rem', margin: 0}}
        >
          {'loading'}
        </ReactPlaceholder>
      </Td>
    ));
    const loadingRows = [];
    for (let i = 0; i < limit; i++) {
      prepareRow(loadingColumns);
      loadingRows.push(<tr key={i}>{loadingColumns}</tr>);
    }
    return loadingRows;
  };

  return (
    <BorderedTable striped bordered hover responsive borderless className="data-table" {...getTableProps()}>
      <Thead className="table-header">
        {headerGroups.map(headerGroup => (
          <Tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, index) => {
              const Comp = column.sortable ? PointerTh : Th;
              return (
                <Comp color={theme.datatable.header} isFirst={index === 0} isLast={index === headerGroup.headers.length - 1} {...column.getHeaderProps()}
                  onClick={() => column.sortable && onClickSortable(column.id, column.sortKey)}>
                  {column.render('Header')}
                  {column.sortable && <SortIcon icon={faExchangeAlt} color="white" rotation={90} size="sm"/>}
                </Comp>
              );
            })}
          </Tr>
        ))}
      </Thead>
      <tbody {...getTableBodyProps()}>
        {error && (
          <tr>
            <td colSpan={columns.length}><Error error={t('error')}/></td>
          </tr>
        )}

        {(loading || forceLoading) ? displayLoading() : (
          rows.length === 0 ? (
            <tr>
              <CenteredTd colSpan={columns.length}>{t('datatable.noResult')}</CenteredTd>
            </tr>
          ) :
            rows.map((row, index) => {
              prepareRow(row);
              return (
                <tr key={index} {...row.getRowProps()}>
                  {row.cells.map(cell => {
                    return <Td {...cell.getCellProps()}>{cell.render('Cell')}</Td>;
                  })}
                </tr>
              );
            })
        )}
      </tbody>
    </BorderedTable>
  );
};

DatatableCore.propTypes = {
  t: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  theme: PropTypes.shape({
    datatable: PropTypes.shape({
      header: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  onClickSortable: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.object,
  result: PropTypes.object,
  forceLoading: PropTypes.bool,
  limit: PropTypes.number,
};

DatatableCore.defaultProps = {
  onClickSortable: () => {},
  fetchPolicy: 'cache-first',
  limit: 10,
};

export default compose(
  withTranslation('common'),
  withTheme,
)(DatatableCore);

